import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shield, ArrowLeft, Rocket, Check } from "lucide-react";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getPlan } from "@/lib/planLimits";
import { validateEmail, isSafeInput } from "@/lib/validation";
import ThemeToggle from "@/components/ThemeToggle";

const countries = ["United States", "United Kingdom", "Canada", "Australia", "South Africa", "Germany", "France", "India", "Brazil", "Nigeria", "Kenya", "UAE", "Singapore", "Other"];

const AppSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromApplication = searchParams.get("from") === "application";
  const selectedPlan = searchParams.get("plan");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please agree to the Terms of Service"); return; }

    const form = formRef.current;
    if (!form) return;

    const data = new FormData(form);
    const firstName = data.get("firstName") as string;
    const lastName = data.get("lastName") as string;
    const email = data.get("email") as string;
    const phone = data.get("phone") as string;
    const company = data.get("company") as string;
    const password = data.get("password") as string;
    const confirmPassword = data.get("confirmPassword") as string;

    if (!firstName || !lastName || !email || !password) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!validateEmail(email)) { toast.error("Please enter a valid email address"); return; }
    if (!isSafeInput(firstName) || !isSafeInput(lastName) || !isSafeInput(company)) {
      toast.error("Invalid characters detected in your input.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: `${firstName} ${lastName}`,
            phone,
            company,
            role: "business_owner", // internal role key unchanged
          },
        },
      });

      if (authError) {
        toast.error(authError.message);
        setLoading(false);
        return;
      }

      if (authData.user) {
        if (phone) {
          await supabase
            .from("profiles")
            .update({ phone })
            .eq("id", authData.user.id);
        }

        // Send signup notification email
        supabase.functions.invoke("notify-signup", {
          body: {
            type: "signup",
            data: {
              firstName,
              lastName,
              email,
              phone,
              company,
              plan: selectedPlan || "trial",
            },
          },
        }).catch(() => {}); // fire and forget

        setLoading(false);
        setSuccess(true);
        toast.success("Account created successfully!");
        setTimeout(() => navigate("/app/business-owner"), 2500);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen app-bg relative overflow-hidden font-outfit">
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `linear-gradient(hsl(var(--app-border-subtle)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--app-border-subtle)) 1px, transparent 1px)`,
        backgroundSize: "60px 60px"
      }} />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20" 
        style={{ background: "radial-gradient(ellipse, hsl(var(--app-gold) / 0.3), transparent 70%)" }} />

      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 py-8 sm:py-16">
        <div className="flex items-center justify-between mb-8 sm:fixed sm:top-6 sm:left-6 sm:z-20 sm:mb-0">
          <button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-2 py-2 px-3 rounded-lg text-sm text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))] hover:bg-white/5 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> <span className="sm:inline">Back to website</span>
          </button>
          <div className="sm:hidden">
            <ThemeToggle />
          </div>
        </div>

        <div className="animate-fade-in">
          {success ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-[hsl(var(--app-green)/0.15)] border-2 border-[hsl(var(--app-green))] flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-[hsl(var(--app-green))]" />
              </div>
              <h2 className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">Welcome to SecureFlex!</h2>
              <p className="text-[hsl(var(--app-text-secondary))] mt-2 text-sm">Setting up your dashboard…</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <img src={secureflexLogo} alt="SecureFlex Logo" className="h-16 w-16 sm:h-24 sm:w-24 object-contain mx-auto mb-4 drop-shadow-[0_0_15px_hsl(var(--app-gold)/0.4)]" />
                <h1 className="font-bebas text-3xl sm:text-5xl text-[hsl(var(--app-text-primary))] max-w-lg mx-auto leading-[1.1]">
                  {selectedPlan ? `GET STARTED WITH ${selectedPlan.toUpperCase()}` : 'START YOUR FREE 30-DAY TRIAL'}
                </h1>
                <p className="text-sm font-semibold tracking-[3px] uppercase text-[hsl(var(--app-gold))] mt-2">30 Days · No Credit Card · Full Access</p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                  {["No Card Required", "30 Days Full Access", "Cancel Anytime"].map((chip) => (
                    <span key={chip} className="px-3 py-1.5 rounded-full text-xs border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] app-card-nested">{chip}</span>
                  ))}
                </div>
              </div>

              {fromApplication && (
                <div className="mb-6 p-4 rounded-xl border border-[hsl(var(--app-green))] bg-[hsl(var(--app-green)/0.08)]">
                  <p className="text-sm text-[hsl(var(--app-green))] font-medium">Application received — your trial access is ready. Complete your account below to get started immediately.</p>
                </div>
              )}

              {selectedPlan && (
                <div className="mb-6 p-4 rounded-xl border border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--app-gold)/0.15)] border border-[hsl(var(--app-gold)/0.3)] flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-[hsl(var(--app-gold))]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[hsl(var(--app-gold))] uppercase">{getPlan(selectedPlan).name} Plan Selected</p>
                    <p className="text-xs text-[hsl(var(--app-text-secondary))]">
                      {getPlan(selectedPlan).price}/mo — {getPlan(selectedPlan).desc}. Start with a free 30-day trial first.
                    </p>
                  </div>
                </div>
              )}

              <form ref={formRef} onSubmit={handleSubmit} className="app-card rounded-2xl p-6 md:p-8 space-y-4" style={{ boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
                <div className="h-px -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignupInput name="firstName" placeholder="First Name" required />
                  <SignupInput name="lastName" placeholder="Last Name" required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignupInput name="email" placeholder="Email Address" type="email" required />
                  <SignupInput name="phone" placeholder="Phone Number" type="tel" required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignupInput name="company" placeholder="Company Name" required />
                  <SignupInput name="companyReg" placeholder="Company Registration (optional)" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignupSelect name="numOfficers" placeholder="Number of Officers" options={["1–5", "6–15", "16–30", "31–50", "50+"]} />
                  <SignupSelect name="numSites" placeholder="Number of Sites" options={["1–2", "3–5", "6–10", "10+"]} />
                </div>
                <SignupSelect name="country" placeholder="Country" options={countries} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SignupInput name="password" placeholder="Password" type="password" autoComplete="new-password" required />
                  <SignupInput name="confirmPassword" placeholder="Confirm Password" type="password" autoComplete="new-password" required />
                </div>
                <textarea
                  name="about"
                  placeholder="Tell us about your operation"
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit resize-none"
                />

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-[hsl(var(--app-gold))]" />
                  <span className="text-xs text-[hsl(var(--app-text-secondary))]">I agree to the <a href="/terms" target="_blank" className="text-[hsl(var(--app-gold))] underline hover:brightness-125">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-[hsl(var(--app-gold))] underline hover:brightness-125">Privacy Policy</a></span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-5 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold text-lg hover:brightness-110 hover:-translate-y-0.5 transition-all gold-glow disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-[hsl(var(--app-bg))] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Rocket className="w-5 h-5" /> Start My Free 30-Day Trial — No Card Required</>
                  )}
                </button>
              </form>

              <p className="text-center mt-6 text-sm text-[hsl(var(--app-text-secondary))]">
                Already have an account? <button onClick={() => navigate("/app")} className="text-[hsl(var(--app-gold))] hover:underline">Sign in →</button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SignupInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit" />
);

const SignupSelect = ({ placeholder, options, name }: { placeholder: string; options: string[]; name: string }) => (
  <select name={name} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit" defaultValue="">
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => <option key={o} value={o} className="text-[hsl(var(--app-text-primary))]">{o}</option>)}
  </select>
);

export default AppSignup;
